import Model, { attr, belongsTo } from '@ember-data/model';
import { isPresent } from '@ember/utils';
import currency from 'currency.js';
import FundSummaryRollingGoalModel from './fund-summary-rolling-goal';
import FundSummaryYtdGoalModel from './fund-summary-ytd-goal';

export default class FundSummaryModel extends Model {
  @attr('string') declare fundId: string;
  @attr('string') declare fundName: string;
  @attr('number') balance?: number;
  @attr('string') goalType?: string;
  @attr('number') monthlyGoal?: number;
  @attr() monthlyData: any;
  @attr('number') lastYearAvgAmount?: number;
  @attr('number') pledgedNoGiftsAmount?: number;
  @attr('number') pledgedDelinquentAmount?: number;
  @attr('number') pledgedGivingAmount?: number;
  @attr('number') pledgedMonthlyAmount?: number;

  @belongsTo('fund-summary-rolling-goal', { async: false, inverse: null })
  rollingGoal?: FundSummaryRollingGoalModel;
  @belongsTo('fund-summary-ytd-goal', { async: false, inverse: null })
  ytdGoal?: FundSummaryYtdGoalModel;

  get annualGoal() {
    return currency(this.monthlyGoal || 0).multiply(12).value;
  }

  get fundNameLowerCase() {
    return (this.fundName || '').toLowerCase();
  }

  get hasMonthlyGoal() {
    return isPresent(this.monthlyGoal);
  }

  lastYearAnnualAmount() {
    return currency(this.lastYearAvgAmount || 0).multiply(12).value;
  }

  get lastYearAvgPercentage() {
    if (!this.hasMonthlyGoal) {
      return 0;
    }
    let lastYearAvgAmount = this.lastYearAvgAmount || 0;
    return Math.round((lastYearAvgAmount * 100) / this.monthlyGoal!);
  }

  get pledgedNoGiftsPercent() {
    if (!this.hasMonthlyGoal) {
      return 0;
    }
    return Math.round(
      ((this.pledgedNoGiftsAmount || 0) * 100) / this.monthlyGoal!,
    );
  }

  get pledgedDelinquentPercent() {
    if (!this.hasMonthlyGoal) {
      return 0;
    }
    return Math.round(
      ((this.pledgedDelinquentAmount || 0) * 100) / this.monthlyGoal!,
    );
  }

  get pledgedGivingPercent() {
    if (!this.hasMonthlyGoal) {
      return 0;
    }
    return Math.round(
      ((this.pledgedGivingAmount || 0) * 100) / this.monthlyGoal!,
    );
  }

  get pledgedAllAmount() {
    return (
      (this.pledgedNoGiftsAmount || 0) +
      (this.pledgedDelinquentAmount || 0) +
      (this.pledgedGivingAmount || 0)
    );
  }

  get pledgedAllPercent() {
    if (!this.hasMonthlyGoal) {
      return 0;
    }
    return Math.round(((this.pledgedAllAmount || 0) * 100) / this.monthlyGoal!);
  }

  get pledgedAnnualAmount() {
    return currency(this.pledgedMonthlyAmount || 0).multiply(12).value;
  }

  get pledgedMonthlyPercent() {
    if (!this.hasMonthlyGoal) {
      return 0;
    }
    return Math.round(
      ((this.pledgedMonthlyAmount || 0) * 100) / this.monthlyGoal!,
    );
  }
}

declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'fund-summary': FundSummaryModel;
  }
}
